import { getField, updateField } from 'vuex-map-fields'
import api from '@/lib/api'
import { NOT_STARTED, IN_PROGRESS, COMPLETE, ERROR } from '@/lib/constants/actionStatuses'
import { PATH_WORKFLOW_TEMPLATES } from '@/lib/constants/api'
import getWorkflowTemplateModel from './models/workflowTemplate'
import updateStatus from '@/store/helpers/updateStatus'
import { notifySuccess, notifyError } from '@/lib/notifications'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'

const workflowTemplate = {
  namespaced: true,
  state: {
    ...getWorkflowTemplateModel()
  },
  getters: {
    getField
  },
  mutations: {
    updateField,
    updateStatus,
    clearWorkflowTemplate: (state) => {
      const model = getWorkflowTemplateModel()
      Object.keys(model).forEach(key => { state[key] = model[key] })
    },
    setWorkflowTemplate: (state, template) => {
      state.id = template.id
      state.name = template.name
      state.typeId = template.typeId
      state.typeName = template.typeName
      state.clientId = template.clientId
      state.clientName = template.clientName
      state.isActive = template.isActive
      state.defaultAdminUserId = template.defaultAdminUserId
      state.defaultAdminUserName = template.defaultAdminUserName
      state.dealAssignmentTypeMatch = template.dealAssignmentTypeMatch
      state.contractTypeMatch = template.contractTypeMatch
      state.tasks = template.tasks || []
      state.createdBy = template.createdBy
      state.updatedBy = template.updatedBy
      state.createdAt = template.createdAt
      state.updatedAt = template.updatedAt
    }
  },
  actions: {
    save ({ state }) {
      const { name, typeId, clientId, isActive, defaultAdminUserId, dealAssignmentTypeMatch, contractTypeMatch, tasks } = state
      return api.doPost({ path: PATH_WORKFLOW_TEMPLATES, data: { name, typeId, clientId, isActive, defaultAdminUserId, dealAssignmentTypeMatch, contractTypeMatch, tasks } })
    },
    update ({ state }) {
      const { id, name, typeId, clientId, isActive, defaultAdminUserId, dealAssignmentTypeMatch, contractTypeMatch, tasks } = state
      return api.doPut({ path: PATH_WORKFLOW_TEMPLATES, data: { id, name, typeId, clientId, isActive, defaultAdminUserId, dealAssignmentTypeMatch, contractTypeMatch, tasks } })
    },
    getWorkflowTemplate: ({ commit, dispatch }, { id }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Loading template, please wait...' })

      return api.doGet({ path: `${PATH_WORKFLOW_TEMPLATES}/${id}` })
        .then(json => json.data)
        .then(template => {
          commit('setWorkflowTemplate', template)
          commit('updateStatus', { status: COMPLETE })
          return template
        }).catch((error) => {
          commit('updateStatus', { status: ERROR, error })
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    saveWorkflowTemplate: ({ state, commit, dispatch }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Saving template, please wait...' })

      const actionName = state.id ? 'update' : 'save'

      return dispatch(actionName).then(response => {
        if (!response.data) {
          if (response.errorMessage) {
            throw new Error(response.errorMessage)
          }
          throw new Error('Unable to save template. Check required fields.')
        }

        commit('clearWorkflowTemplate')
        commit('updateStatus', { status: COMPLETE })
        notifySuccess(dispatch, 'Template saved')

        return response
      }).catch((error) => {
        commit('updateStatus', { status: ERROR, error })
        notifyError(dispatch, error || 'Unable to save template. Try again later')
        throw error
      })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    copyWorkflowTemplate: ({ dispatch }, { sourceTemplateId, name, clientId }) => {
      showProgressModal({ dispatch, message: 'Copying template, please wait...' })
      return api.doPost({
        path: `${PATH_WORKFLOW_TEMPLATES}/${sourceTemplateId}/copy`,
        data: { name, clientId }
      })
        .then(response => {
          if (!response.data) {
            throw new Error('Unable to create workflow template for this client.')
          }
          return response.data
        })
        .catch(error => {
          notifyError(dispatch, error || 'Unable to create workflow template')
          throw error
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    clearWorkflowTemplate: ({ commit }) => {
      commit('clearWorkflowTemplate')
      return commit('updateStatus', { status: NOT_STARTED })
    }
  }
}

export default workflowTemplate
